import { ViewIds } from '@ardoq/api-types';
import { WILDCARD } from '@ardoq/common-helpers';
import {
  CollapsibleGraphGroup,
  ComponentGraphGroup,
  ComponentGraphNode,
  GraphItem,
  GraphItemsModel,
  GraphNode,
} from '@ardoq/graph';
import { logError } from '@ardoq/logging';
import { dispatchAction } from '@ardoq/rxbeach';
import { updateViewSettings } from '@ardoq/view-settings';
import { isDescendant } from 'modelInterface/graph/utils';

type GetNewCollapsedGroupIdsArgs = {
  oldCollapsedGroupIds: string[];
  allGroupIds: string[];
  toggledGroup: CollapsibleGraphGroup | ComponentGraphGroup;
};
export const getNewCollapsedGroupIdsAfterToggle = ({
  oldCollapsedGroupIds,
  allGroupIds,
  toggledGroup,
}: GetNewCollapsedGroupIdsArgs) => {
  const previousCollapsedGroupIds =
    oldCollapsedGroupIds.includes(WILDCARD) && toggledGroup.collapsed
      ? allGroupIds
      : oldCollapsedGroupIds;
  // If currently collapsed, we want to expand (remove from list)
  // If currently expanded, we want to collapse (add to list)
  const collapsedGroupIds = toggledGroup.collapsed
    ? previousCollapsedGroupIds.filter(id => id !== toggledGroup.id)
    : [...previousCollapsedGroupIds, toggledGroup.id];

  return collapsedGroupIds;
};

interface ProcessAddedNodesReducerState {
  add: string[];
  update: string[];
  expandedStateChangingGroupId: string;
  byId: Map<string, GraphItem>;
}
export const processAddedNodes = (
  state: ProcessAddedNodesReducerState,
  nodeId: string
) => {
  const isNewItem =
    nodeId !== state.expandedStateChangingGroupId &&
    ((state.expandedStateChangingGroupId === WILDCARD &&
      state.byId.get(nodeId)!.parent) ||
      isDescendant({
        parentId: state.expandedStateChangingGroupId,
        item: state.byId.get(nodeId)!,
      }));
  if (isNewItem) {
    state.add.push(nodeId);
  } else {
    state.update.push(nodeId);
  }
  return state;
};
type GetAddedAndProcessedNodesArgs<
  TNode extends GraphNode | ComponentGraphNode,
  TGroup extends CollapsibleGraphGroup | ComponentGraphGroup,
> = {
  nodes: GraphItemsModel<TNode>;
  groups: GraphItemsModel<TGroup>;
  expandedStateChangingGroupId: string;
};
export const getAddedAndProcessedNodes = <
  TNode extends GraphNode | ComponentGraphNode,
  TGroup extends CollapsibleGraphGroup | ComponentGraphGroup,
>({
  nodes,
  groups,
  expandedStateChangingGroupId,
}: GetAddedAndProcessedNodesArgs<TNode, TGroup>) => {
  const { add: addedNodes, update: updatedNodes } = nodes.add.reduce(
    processAddedNodes,
    {
      add: [],
      update: nodes.update,
      byId: nodes.byId,
      expandedStateChangingGroupId,
    }
  );
  const { add: addedGroups, update: updatedGroups } = groups.add.reduce(
    processAddedNodes,
    {
      add: [],
      update: groups.update,
      byId: groups.byId,
      expandedStateChangingGroupId,
    }
  );
  return { addedNodes, updatedNodes, addedGroups, updatedGroups };
};

const COLLAPSED_GROUPS_LIMIT = 2000;
export const updateCollapsedGroupsViewSettingsState = (
  viewId: ViewIds,
  collapsedGroupIds: string[]
) => {
  if (collapsedGroupIds.length > COLLAPSED_GROUPS_LIMIT) {
    logError(
      Error(
        'Number of CollapsedGroupIds is exceeding the number stored in view settings'
      )
    );
  }

  dispatchAction(
    updateViewSettings({
      viewId,
      settings: {
        collapsedGroupIds: collapsedGroupIds.slice(-COLLAPSED_GROUPS_LIMIT), // only take the COLLAPSED_GROUPS_LIMIT, to prevent the viewSettings from bloating infinitely,
      },
      persistent: true,
    })
  );
};
